import React, {FC} from 'react'

import {Flex} from '../../utils/chakraMonomorphic'

export interface ClickArgs {
  index: number
}

export interface GalleryImageProps {
  src: string
  srcWidth: number
  srcHeight: number
  margin?: number
  fallbackPhoto: string
  rotation?: 0 | 90 | 180 | 270
  processing?: boolean
  clickRef?: React.RefObject<HTMLImageElement>
}

export const GalleryImage: FC<React.PropsWithChildren<GalleryImageProps>> = (
  props,
) => {
  const {
    src,
    srcWidth,
    srcHeight,
    margin,
    children,
    fallbackPhoto,
    rotation,
    processing,
    clickRef,
  } = props

  const imgStyle = {
    margin,
    opacity: 1,
    display: 'block',
    transition:
      'transform .135s cubic-bezier(0.0,0.0,0.2,1),opacity linear .15s',
  }

  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  // The decoded thumbnail bitmap. Loaded EXACTLY ONCE per `src` (see the load
  // effect) and reused for every redraw — it must never be re-fetched on a
  // layout change.
  const imageRef = React.useRef<HTMLImageElement | null>(null)
  const [isImageReady, setIsImageReady] = React.useState(false)

  // Load the bitmap once per `src`. Historically the load lived in the same
  // effect as the canvas draw and was keyed on `srcWidth`/`srcHeight` as well —
  // the fractional, container-derived layout dimensions react-photo-gallery
  // recomputes on every ResizeObserver tick. Above ~996px that (unmaintained)
  // library's justified layout can enter a bistable oscillation, flip-flopping
  // those dimensions every frame; keying the load on them re-ran `new Image()`
  // ~60x/s, which hammered the network and blanked the canvas — the reported
  // flicker (AB#22131). PR 9549 stabilized the URL string (turning the S3
  // misses into browser-cache hits) but left this load-on-relayout loop intact.
  // Keying the load solely on `src` breaks it: layout churn no longer reloads.
  React.useEffect(() => {
    let cancelled = false
    setIsImageReady(false)
    imageRef.current = null

    const image = new Image()
    image.onload = (): void => {
      if (cancelled) return
      imageRef.current = image
      setIsImageReady(true)
    }
    image.onerror = (): void => {
      if (cancelled) return
      // The previous canvas `onError` handler never fired — a <canvas> emits no
      // `error` event — so a broken thumbnail silently rendered nothing. Fall
      // back here, on the actual image load.
      const fallback = new Image()
      fallback.onload = (): void => {
        if (cancelled) return
        imageRef.current = fallback
        setIsImageReady(true)
      }
      fallback.src = fallbackPhoto
    }
    image.src = src

    return (): void => {
      cancelled = true
      image.onload = null
      image.onerror = null
    }
  }, [src, fallbackPhoto])

  // Draw (and redraw on rotation / layout-size change) from the already-decoded
  // bitmap — no network. useLayoutEffect (not useEffect) so the redraw lands in
  // the same frame as React's canvas-size commit: assigning <canvas
  // width/height> auto-clears the bitmap, and redrawing pre-paint means that
  // clear is never visible. Each redraw is a cheap cached-bitmap drawImage, so
  // even while react-photo-gallery is still oscillating the canvas no longer
  // flickers or refetches.
  React.useLayoutEffect(() => {
    const canvas = canvasRef.current
    const image = imageRef.current
    if (!canvas || !image) return

    const context = canvas.getContext('2d')
    if (!context) return

    // Clear first so a rotation/resize redraw doesn't stack images.
    context.clearRect(0, 0, srcWidth, srcHeight)

    // save()/restore() bracket the origin translate + rotate so they don't leak
    // into the next draw.
    context.save()

    // Move the origin to the canvas center; the offsets in drawImage() then
    // place the image's corner correctly.
    context.translate(srcWidth / 2, srcHeight / 2)

    if (rotation) {
      context.rotate((rotation * Math.PI) / 180) // angle in radians
    }

    // Height and width swap when the orientation is rotated 90/270.
    if (!rotation || rotation === 180) {
      context.drawImage(
        image,
        -srcWidth / 2,
        -srcHeight / 2,
        srcWidth,
        srcHeight,
      )
    } else {
      context.drawImage(
        image,
        -srcHeight / 2,
        -srcWidth / 2,
        srcHeight,
        srcWidth,
      )
    }

    context.restore()
  }, [isImageReady, srcHeight, srcWidth, rotation])

  return (
    <>
      <Flex position='relative' flexDir='row' alignItems='flex-start'>
        {processing && (
          <Flex
            alignItems='flex-end'
            padding='5px'
            style={{
              color: 'white',
              position: 'absolute',
              height: '100%',
              width: '100%',
              backgroundColor: 'black',
              opacity: 0.6,
            }}
          />
        )}
        {/* canvas needed to do client-side rotate, but needs click handler for hidden image to work with lightbox */}
        <canvas
          ref={canvasRef}
          width={srcWidth}
          height={srcHeight}
          style={imgStyle}
          onClick={() => clickRef?.current?.click()}
        />
        {children}
      </Flex>
      {/* hidden image to let lightbox to work */}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img src={src} ref={clickRef} style={{display: 'none'}} />
    </>
  )
}
